import {useEffect, useRef} from 'react';
import {useLocation, useNavigate, useNavigationType} from 'react-router-dom';
import isEqual from 'lodash.isequal';
import {useFilterContext} from "../infrastructure/FilterContext";
import {parseQueryToFilters, serializeFiltersToQuery} from "../infrastructure/filterUrlMapper";

/**
 * Sincroniza filtros del catalogo <-> URL en /articulos.
 *
 * Reglas (revisadas a fondo el 12-ago tras varios sustos de estados fantasma):
 * - URL -> contexto SOLO cuando la URL es la fuente legitima: al entrar con el contexto
 *   vacio (deep link / recarga) o cuando el CAMBIO de URL viene del propio usuario
 *   navegando con atras/adelante (navigationType POP). Antes el "atras" dejaba URL y
 *   lista descoordinadas, y una recarga sobre esa URL vieja resucitaba busquedas
 *   fantasma.
 * - contexto -> URL en cualquier otro cambio de filtros (replace, sin ensuciar
 *   historial). La comparacion usa siempre el location del render actual (esta en las
 *   dependencias): comparar contra una URL rancia provocaba reescrituras de mas.
 * - Cuando la URL acaba de mandar (hidratacion), el efecto contexto->URL de ESE mismo
 *   ciclo se salta una vez (aun ve los filtros viejos y desharia el atras/adelante).
 * - updateFilters ignora contenidos identicos (FilterContext), asi que estos efectos no
 *   pueden alimentarse en bucle: cada vuelta sin cambios reales se corta.
 */
export const useFilterUrlSync = () => {
    const {filters, updateFilters} = useFilterContext();
    const location = useLocation();
    const navigate = useNavigate();
    const navigationType = useNavigationType();
    const urlAcabaDeMandar = useRef(false);

    // URL -> contexto (deep link, recarga o atras/adelante del navegador).
    useEffect(() => {
        if (!location.pathname.startsWith('/articulos')) return;

        const urlFilters = parseQueryToFilters(new URLSearchParams(location.search));
        const isContextEmpty = Object.keys(filters).length === 0;
        const urlManda = isContextEmpty || navigationType === 'POP';

        if (urlManda && !isEqual(urlFilters, filters)) {
            urlAcabaDeMandar.current = true;
            updateFilters(urlFilters);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.search]);

    // contexto -> URL.
    useEffect(() => {
        if (!location.pathname.startsWith('/articulos')) return;
        if (urlAcabaDeMandar.current) {
            // La hidratacion de arriba va un render por delante de estos filtros.
            urlAcabaDeMandar.current = false;
            return;
        }

        const serialized = serializeFiltersToQuery(filters).toString();
        const current = new URLSearchParams(location.search).toString();

        if (serialized !== current) {
            navigate(
                {
                    pathname: location.pathname,
                    search: serialized ? `?${serialized}` : '',
                },
                {replace: true}
            );
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filters, location.pathname, location.search]);
};
